import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, Newspaper, CalendarDays, Landmark } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const SUGGESTIONS = [
  { to: '/news', label: 'Latest news', icon: Newspaper },
  { to: '/events', label: "What's on", icon: CalendarDays },
  { to: '/history', label: 'Paisley history', icon: Landmark },
  { to: '/search', label: 'Search the site', icon: Search },
];

/**
 * Genuine "page not found" screen. Replaces the old catch-all redirect to the
 * homepage, which answered dead URLs with a 200 and the homepage content —
 * the soft-404 pattern search engines penalise. noIndex keeps these URLs out
 * of the index.
 */
const NotFoundPage = () => (
  <div className="min-h-screen bg-white flex flex-col" data-testid="not-found-page">
    <SEO
      title="Page not found | Paisley.org.uk"
      description="That page no longer exists on Paisley.org.uk. Browse the latest Paisley news, events and history instead."
      url="/404"
      noIndex
    />
    <Header />
    <main className="flex-1 max-w-3xl mx-auto px-6 py-20 md:py-28">
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-cyan-700 mb-3">Error 404</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
        We cannot find that page
      </h1>
      <p className="mt-5 text-base text-slate-600 max-w-xl">
        The link may be out of date, or the page may have moved when the site was rebuilt.
        Nothing is lost — the archive is all still here.
      </p>

      <div className="mt-10 grid gap-3 sm:grid-cols-2">
        {SUGGESTIONS.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="group flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3.5 hover:border-cyan-400 hover:bg-slate-50 transition-colors"
            data-testid={`not-found-link-${to.replace('/', '')}`}
          >
            <Icon size={18} className="text-cyan-600" />
            <span className="text-sm font-semibold text-slate-900">{label}</span>
          </Link>
        ))}
      </div>

      <Link
        to="/"
        className="mt-8 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-5 py-3 rounded-lg transition-colors"
        data-testid="not-found-home"
      >
        <Home size={15} /> Back to the homepage
      </Link>
    </main>
    <Footer />
  </div>
);

export default NotFoundPage;
